import type { useStyletron } from '@thanx/uikit/theme'
import { buildTranslate } from 'locales'
import type { ConsolidatedLocation, LocationStatus } from '../mockLocations'
import type { SortKey } from './types'

const t = buildTranslate('locations.location_list')

// Localised label for a location-status badge. Centralising this lets the
// sort comparator and the badge renderer share one source of truth, so a
// future copy change can't drift the rendered text out of sync with what
// the comparator orders on.
export const locationStatusLabel = (status: LocationStatus): string => {
  if (status.kind === 'healthy') return t('badge.healthy') as string
  // Pick the singular/plural key explicitly — react-redux-i18n's automatic
  // _one/_other pluralisation isn't wired up here, so picking the suffix
  // ourselves keeps "1 notice" reading correctly while "3 errors" stays
  // plural.
  const suffix = status.count === 1 ? 'one' : 'other'
  return t(`badge.${status.kind}_${suffix}`, {
    count: status.count,
  }) as string
}

// Status columns sort on (kind, count) tuples rather than the displayed
// label. Lexical compare on labels like "10 errors" / "2 errors" breaks
// at double digits ("10" < "2" character-by-character); ranking by kind
// and breaking ties numerically keeps the order consistent at any
// merchant size. Ordering-status column reads its label through
// `t()` so non-English locales sort against what's actually on screen.
export const KIND_RANK: Record<LocationStatus['kind'], number> = {
  healthy: 0,
  notices: 1,
  errors: 2,
}

export const compareLocations = (
  a: ConsolidatedLocation,
  b: ConsolidatedLocation,
  key: SortKey
): number => {
  if (key === 'name') return a.name.localeCompare(b.name)
  if (key === 'locationStatus') {
    const kindDiff =
      KIND_RANK[a.locationStatus.kind] - KIND_RANK[b.locationStatus.kind]
    if (kindDiff !== 0) return kindDiff
    const aCount = 'count' in a.locationStatus ? a.locationStatus.count : 0
    const bCount = 'count' in b.locationStatus ? b.locationStatus.count : 0
    return aCount - bCount
  }
  const orderingLabel = (loc: ConsolidatedLocation): string =>
    loc.orderingEnabled
      ? (t('badge.ordering_enabled') as string)
      : (t('badge.ordering_disabled') as string)
  return orderingLabel(a).localeCompare(orderingLabel(b))
}

export const badgeBaseClass = (css: ReturnType<typeof useStyletron>[0]) =>
  `d-inline-flex align-items-center px-s py-xxs border-radius-5 ${css({
    fontSize: '10px',
    fontWeight: 700,
    letterSpacing: '1px',
    textTransform: 'uppercase',
    lineHeight: '12px',
    whiteSpace: 'nowrap',
  })}`
